import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';

import { Country } from '@xemplo/country-helper';

import {
  handleInitialCountryCode,
  tryParsePhoneNumber,
} from '../helpers/phone-field.helper';
import { isValidPhone } from '../helpers/validation.helper';
import { PhoneFieldRef, PhoneFieldRhfProps } from '../phone-field.types';

export function usePhoneFieldRhf(
  field: ControllerRenderProps<FieldValues, string>,
  props: PhoneFieldRhfProps,
) {
  const {
    name,
    rules,
    onBlur,
    onChange,
    onValueChange,
    invalidPhoneNumberMessage = 'Invalid phone number',
  } = props;
  const formContext = useFormContext();
  const [isReady, setIsReady] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const isFirstRender = useRef(true);
  const isClearingField = useRef(false);
  const phoneFieldRef = useRef<PhoneFieldRef>(null);
  const defaultCountryCode = useMemo(
    () => handleInitialCountryCode(field.value, props.defaultCountryCode),
    // Intentionally ignoring dependency on field.value
    // as it would cause unnecessary recalculations and re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.defaultCountryCode],
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState(defaultCountryCode);

  const { onBlur: fieldOnBlur, onChange: fieldOnChange } = field;

  const isRequired = useMemo(() => !!rules?.required, [rules]);

  const isPhoneValid = useCallback(
    (value: string) => {
      // If the field is not required and the value is empty, consider it valid
      if (!isRequired && !value) return true;
      return isValidPhone(value, selectedCountryCode) ? true : invalidPhoneNumberMessage;
    },
    [selectedCountryCode, invalidPhoneNumberMessage, isRequired],
  );

  // Recreate the rules to account for the country code changes
  // Merge existing rules with the dynamic isPhoneValid rule
  // The isPhoneValid rule can be overriden by user-provided rules
  const registerRules = useCallback(() => {
    const mergedRules = { ...rules, validate: { isPhoneValid, ...rules?.validate } };
    formContext.register(name, mergedRules);
  }, [rules, isPhoneValid, formContext, name]);

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      fieldOnBlur(); // for RHF validation
      onBlur?.(e);
    },
    [fieldOnBlur, onBlur],
  );

  // This handles basic input changes for display purposes
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      isFirstRender.current = false; // user has started interacting, skip init override
      const newValue = e.target.value;
      setDisplayValue(newValue);
      onChange?.(e);
    },
    [onChange],
  );

  // This syncs the internal value with RHF form state
  const handleValueChange = useCallback(
    (value: string) => {
      // Track when WE are clearing the field (partial/unparseable input or empty input)
      // so the effect can distinguish this from a programmatic form reset
      if (!value) isClearingField.current = true;
      fieldOnChange(value);
      onValueChange?.(value);
    },
    [fieldOnChange, onValueChange],
  );

  const handleCountryChange = useCallback((countryCode: Country.Code) => {
    setSelectedCountryCode(countryCode);
  }, []);

  useEffect(() => {
    // Handle reset: when RHF clears the field
    if (!field.value) {
      const wasOurClear = isClearingField.current;
      isClearingField.current = false;
      // If WE set field.value to "" (partial/empty input), skip the reset —
      // the display is already correct and the country should not change.
      // Only reset when it came from an external source (programmatic form reset).
      if (!wasOurClear) {
        phoneFieldRef.current?.setSelectedCountry(defaultCountryCode);
        setDisplayValue('');
      }
      setIsReady(true);
      return;
    }

    // Clear the flag whenever field.value becomes truthy so future resets work correctly
    isClearingField.current = false;

    // Handle initialization: parse initial value only on first render.
    // isFirstRender is set to false in handleChange so typing never triggers this path.
    if (isFirstRender.current) {
      isFirstRender.current = false;

      const parsed = tryParsePhoneNumber(field.value, defaultCountryCode);
      if (parsed) {
        setDisplayValue(parsed.nationalNumber || '');
      }
    }

    setIsReady(true);
  }, [defaultCountryCode, field.value]);

  useEffect(() => {
    registerRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryCode]);

  return {
    isReady,
    defaultCountryCode,
    displayValue,
    phoneFieldRef,
    handleBlur,
    handleChange,
    handleValueChange,
    handleCountryChange,
  };
}
